import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Clock,
  FlaskConical,
  Printer,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import {
  type AttentionItem,
  type DashboardSnapshot,
  type WorklistRow,
  PIN_PREFIX,
  reportProgress,
} from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { EmptyState } from '#client/components/ui/EmptyState';
import { DueCell } from '#client/components/ui/DueCell';
import { Identifier } from '#client/components/ui/Identifier';
import { Progress } from '#client/components/ui/Progress';
import { Skeleton } from '#client/components/ui/Skeleton';
import { Sparkline } from '#client/components/ui/Sparkline';
import { useCan } from '#client/hooks/useCan';
import { formatCount } from '#client/lib/format';
import { routes } from '#client/lib/routes';
import { sumValues } from './dashboard.logic';
import { useLabSnapshot } from './useDashboard';

/** The dashboard's cards, each given only the slice of the snapshot it shows. */

export function QueueCard({ queue }: { queue: DashboardSnapshot['my_queue'] }) {
  return (
    <Card
      title={queue.heading}
      subtitle={`${queue.total} open`}
      flush
      actions={
        // A technician's queue is the lab's queue, so "See all" goes to the
        // open work rather than to `mine=true`, which would be empty for them.
        // The server chooses the link alongside the heading.
        queue.total > 0 ? (
          <Link className="c-card__link" to={queue.href}>
            See all
          </Link>
        ) : undefined
      }
    >
      {queue.items.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 />}
          tone="positive"
          title="Nothing waiting on you"
          body="Every report you are responsible for has been dealt with."
        />
      ) : (
        <div className="c-queue">
          {queue.items.map((row) => (
            <QueueRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </Card>
  );
}

export function AttentionCard({ attention }: { attention: AttentionItem[] }) {
  return (
    <Card title="Needs attention" flush>
      {attention.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 />}
          tone="positive"
          title="Nothing needs attention"
          body="No overdue work, no stalled registrations, no unpaid completed reports."
        />
      ) : (
        <div className="c-queue">
          {attention.map((item) => (
            <AttentionRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </Card>
  );
}

export function QuickActionsCard() {
  const can = useCan();

  return (
    <Card title="Quick actions">
      <div className="c-quick-actions">
        {can('patient.create') && (
          <QuickAction to={routes.patientNew} icon={<UserPlus />} label="Register patient" />
        )}
        <QuickAction to={routes.worklist()} icon={<ClipboardList />} label="Open worklist" />
        <QuickAction
          to={routes.worklist({ overdue: true })}
          icon={<AlertTriangle />}
          label="Overdue work"
        />
        <QuickAction to={routes.patients} icon={<Printer />} label="Find a patient" />
        {can('labTest.create') && (
          <QuickAction to={routes.labTestNew} icon={<FlaskConical />} label="Add lab test" />
        )}
      </div>
    </Card>
  );
}

export function LabPulseCard({ pulse }: { pulse: DashboardSnapshot['lab_pulse'] }) {
  return (
    <Card title="Lab pulse" subtitle="Last 7 days">
      {/* Two equal columns. The ranking is its own card rather than a
          cramped third column here. */}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <PulseChart
          label="Registrations"
          values={pulse.registrations.map((point) => point.count)}
        />
        <PulseChart label="Completions" values={pulse.completions.map((point) => point.count)} />
      </div>
    </Card>
  );
}

/**
 * The lab's size, behind a collapsed summary. Fetched the first time it is
 * opened rather than with the dashboard: the whole-table counts behind it cost
 * more than everything else on the page.
 */
export function SnapshotCard() {
  const [open, setOpen] = useState(false);
  const { data: snapshot, error } = useLabSnapshot(open);

  return (
    <Card flush>
      <details
        className="c-snapshot"
        onToggle={(event) => {
          if (event.currentTarget.open) setOpen(true);
        }}
      >
        <summary className="c-snapshot__summary">Lab snapshot</summary>
        <div className="c-snapshot__body">
          {error && <p className="c-snapshot__note">{error}</p>}
          {!snapshot && !error && <Skeleton width="60%" height="1rem" />}
          {snapshot && (
            <>
              <dl className="c-detail-list">
                <SnapshotItem label="Patients" value={snapshot.patients_total} />
                <SnapshotItem label="Reports" value={snapshot.reports_total} />
                <SnapshotItem label="Lab tests" value={snapshot.lab_tests_total} />
                <SnapshotItem label="Panels" value={snapshot.panels_total} />
                <SnapshotItem label="Staff" value={snapshot.staff_total} />
              </dl>

              {/*
                A real configuration gap, surfaced rather than hidden. Reports
                for these tests can never be counted overdue, so a green
                "Nothing overdue" tile would otherwise be quietly incomplete.
              */}
              {snapshot.lab_tests_without_tat > 0 && (
                <p className="c-snapshot__note">
                  <Link to={routes.labTests}>
                    {snapshot.lab_tests_without_tat} of {snapshot.lab_tests_total} lab tests have no
                    turnaround set
                  </Link>{' '}
                  — their reports are never counted as overdue.
                </p>
              )}
            </>
          )}
        </div>
      </details>
    </Card>
  );
}

export function DashboardSkeleton() {
  return (
    <>
      <div className="c-dashboard__kpis">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="c-stat">
            <Skeleton width="55%" height="0.75rem" />
            <Skeleton width="40%" height="1.5rem" />
          </div>
        ))}
      </div>
      <Card flush>
        <div className="c-queue">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="c-queue__item">
              <span className="c-queue__main">
                <Skeleton width="45%" />
                <Skeleton width="65%" />
              </span>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function QueueRow({ row }: { row: WorklistRow }) {
  return (
    <Link className="c-queue__item" to={routes.report(row.id)}>
      <span className="c-queue__main">
        <span className="c-queue__title">{row.patient.name}</span>
        <span className="c-queue__detail">
          <Identifier value={row.patient.pin_no} stripPrefix={PIN_PREFIX} /> · {row.name}
        </span>
      </span>

      <span className="c-queue__meta">
        <Progress {...reportProgress(row)} />
        <DueCell {...row} />
      </span>
    </Link>
  );
}

const ATTENTION_ICONS = {
  unpaid_completed: Wallet,
  overdue_report: AlertTriangle,
  stalled_registration: Clock,
} as const satisfies Record<AttentionItem['kind'], unknown>;

function AttentionRow({ item }: { item: AttentionItem }) {
  const Icon = ATTENTION_ICONS[item.kind];

  return (
    <Link className="c-queue__item" to={item.href}>
      <span className={`c-attention__icon c-attention__icon--${item.tone}`} aria-hidden="true">
        <Icon />
      </span>
      <span className="c-queue__main">
        <span className="c-queue__title">{item.title}</span>
        <span className="c-queue__detail">{item.detail}</span>
      </span>
    </Link>
  );
}

function QuickAction({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  return (
    <Link className="c-quick-action" to={to}>
      {icon}
      {label}
    </Link>
  );
}

function PulseChart({ label, values }: { label: string; values: number[] }) {
  return (
    <div>
      <p className="c-detail-list__label">{label}</p>
      <div className="c-pulse-chart">
        <Sparkline values={values} label={`${label}, last 7 days`} width={220} height={48} />
      </div>
      <p className="c-queue__detail tnum">{sumValues(values)} in the last 7 days</p>
    </div>
  );
}

function SnapshotItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="c-detail-list__item">
      <dt className="c-detail-list__label">{label}</dt>
      <dd className="c-detail-list__value">{formatCount(value)}</dd>
    </div>
  );
}
